import Service, { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import {
  DATE_FORMAT,
  STAY_DATE_GRANULARITIES,
  type StayDateGranularity,
} from 'lumen/utils/constants/net-revenue-constants';
import { NORMALIZED_PERIOD } from 'lumen/utils/constants/ri-constant-values';
import type {
  CostProfileData,
  CostProfileTotals,
  CostTypeRow,
  Kpi,
  KpiData,
  NetRevenueCostProfile,
  NetRevenueInputs,
  NetRevenuePeriods,
  NetRevenueStayDatePoint,
  StayDateComparisonPoint,
  StayDateData,
  StayDatePoint,
} from 'lumen/utils/net-revenue/types';
import safeDivide from 'lumen/utils/numeric/safe-divide';

interface NetRevenueMetrics {
  adr: number;
  cost: number;
  costPercent: number;
  netAdr: number;
  netRevenue: number;
  netRevenuePercent: number;
  roomNights: number;
  roomRevenue: number;
}

/**
 * Derive the full net-revenue metric set from the raw
 * `{ roomNights, roomRevenue, cost }` inputs:
 * `adr`, `netRevenue`, `netAdr`, `costPercent`, `netRevenuePercent`.
 *
 * This is the single place those formulas live; every other method
 * on the service composes its output by calling `deriveMetrics`.
 */
function deriveMetrics(inputs: NetRevenueInputs): NetRevenueMetrics {
  const netRevenue = inputs.roomRevenue - inputs.cost;
  const costPercent = safeDivide(inputs.cost, inputs.roomRevenue) ?? 0;

  return {
    adr: safeDivide(inputs.roomRevenue, inputs.roomNights) ?? 0,
    cost: inputs.cost,
    costPercent,
    netAdr: safeDivide(netRevenue, inputs.roomNights) ?? 0,
    netRevenue,
    netRevenuePercent: safeDivide(netRevenue, inputs.roomRevenue) ?? 0,
    roomNights: inputs.roomNights,
    roomRevenue: inputs.roomRevenue,
  };
}

/**
 * Build a single KPI for the hero strip. `changePercent` is `null`
 * when the period has no comparable data (`comparisonValue` is
 * `undefined`) or when the comparison value is zero (division-by-zero
 * would be meaningless). The UI renders the "no comparison" hyphen
 * instead of a percentage chip in those cases.
 */
function buildKpi(
  currentValue: number,
  comparisonValue: number | undefined,
  overrides: Partial<Kpi> = {},
): Kpi {
  return {
    changePercent:
      comparisonValue === undefined || comparisonValue === 0
        ? null
        : (safeDivide(currentValue - comparisonValue, comparisonValue) ?? 0),
    invertColors: false,
    value: currentValue,
    ...overrides,
  };
}

/**
 * Group a daily stay-date series by week or month, summing numeric
 * fields and labeling each bucket with the first day of its period.
 */
function bucketStayDateSeries(
  points: readonly NetRevenueStayDatePoint[],
  granularity: 'week' | 'month',
): NetRevenueStayDatePoint[] {
  const periodKey = NORMALIZED_PERIOD[granularity];
  const bucketsByPeriodStart = new Map<string, NetRevenueStayDatePoint>();

  points.forEach((point) => {
    const periodStart = dayjs
      .utc(point.date)
      .startOf(periodKey)
      .format(DATE_FORMAT);
    const existingBucket = bucketsByPeriodStart.get(periodStart);
    if (existingBucket) {
      existingBucket.cost += point.cost;
      existingBucket.roomNights += point.roomNights;
      existingBucket.roomRevenue += point.roomRevenue;
      existingBucket.capacity += point.capacity;
      return;
    }
    bucketsByPeriodStart.set(periodStart, {
      capacity: point.capacity,
      cost: point.cost,
      date: periodStart,
      roomNights: point.roomNights,
      roomRevenue: point.roomRevenue,
    });
  });

  return Array.from(bucketsByPeriodStart.values());
}

/** Convert a raw stay-date bucket into the comparison-point shape. */
function toComparisonPoint(
  point: NetRevenueStayDatePoint,
): StayDateComparisonPoint {
  return {
    cost: point.cost,
    netRevenue: point.roomRevenue - point.cost,
    occupancy: safeDivide(point.roomNights, point.capacity) ?? 0,
    roomRevenue: point.roomRevenue,
  };
}

function buildTotalsRow(
  inputs: NetRevenueInputs,
): Omit<CostProfileTotals, 'comparison'> {
  const metrics = deriveMetrics(inputs);
  return {
    cost: metrics.cost,
    costPercent: metrics.costPercent,
    netAdr: metrics.netAdr,
    netRevenue: metrics.netRevenue,
    netRevenuePercent: metrics.netRevenuePercent,
    roomNights: metrics.roomNights,
    roomRevenue: metrics.roomRevenue,
  };
}

/**
 * Build the totals row of the cost-drilldown table from the period's
 * aggregate inputs, including the nested `comparison` totals when present.
 */
function buildCostProfileTotals(
  currentTotals: NetRevenueInputs,
  comparisonTotals: NetRevenueInputs | undefined,
): CostProfileTotals {
  return {
    ...buildTotalsRow(currentTotals),
    comparison: comparisonTotals ? buildTotalsRow(comparisonTotals) : null,
  };
}

export default class NetRevenueDeriveService extends Service {
  @service declare intl: Services['intl'];

  /**
   * Build the cost-drilldown table dataset from a `NetRevenuePeriods`
   * snapshot.
   *
   * - Rows: one per current cost-profile, paired with comparison by
   *   `name` (`comparison: null` when no match).
   * - `remainingReservations`: forwarded from the current period.
   * - `totals`: aggregate footer row, with nested comparison totals.
   *
   * `comparisonLabel` is caller-supplied; pass `null` for "no comparison".
   */
  deriveCostProfile(
    periods: NetRevenuePeriods | undefined,
    comparisonLabel: string | null,
  ): CostProfileData | undefined {
    if (!periods) {
      return undefined;
    }

    const comparisonProfilesByName = new Map<string, NetRevenueCostProfile>(
      periods.comparison?.costProfiles.map((profile) => [
        profile.name,
        profile,
      ]) ?? [],
    );

    const rows: CostTypeRow[] = periods.current.costProfiles.map(
      (currentProfile) => {
        const metrics = deriveMetrics(currentProfile);
        const comparisonMatch = comparisonProfilesByName.get(
          currentProfile.name,
        );
        return {
          adr: metrics.adr,
          comparison: comparisonMatch ? deriveMetrics(comparisonMatch) : null,
          cost: metrics.cost,
          costPercent: metrics.costPercent,
          name: currentProfile.name,
          netAdr: metrics.netAdr,
          netRevenue: metrics.netRevenue,
          netRevenuePercent: metrics.netRevenuePercent,
          roomNights: metrics.roomNights,
          roomRevenue: metrics.roomRevenue,
        };
      },
    );

    return {
      comparisonLabel,
      remainingReservations: periods.current.remainingReservations,
      rows,
      totals: buildCostProfileTotals(
        periods.current.totals,
        periods.comparison?.totals,
      ),
    };
  }

  /**
   * Compute the four hero KPI cards (`netRevenue`, `cost`, `netAdr`,
   * `avgCost`) from a `NetRevenuePeriods` snapshot. Returns
   * `undefined` while the data is still loading — the controller forwards
   * that straight through to the component so the skeleton state
   * shows.
   */
  deriveKpi(periods: NetRevenuePeriods | undefined): KpiData | undefined {
    if (!periods) {
      return undefined;
    }

    const currentTotals = deriveMetrics(periods.current.totals);
    const comparisonTotals = periods.comparison
      ? deriveMetrics(periods.comparison.totals)
      : undefined;

    return {
      avgCost: buildKpi(
        safeDivide(currentTotals.cost, currentTotals.roomNights) ?? 0,
        comparisonTotals
          ? (safeDivide(comparisonTotals.cost, comparisonTotals.roomNights) ??
              0)
          : undefined,
        {
          invertColors: true,
          label: this.intl.t('net_revenue.per_room_night'),
        },
      ),
      cost: buildKpi(currentTotals.cost, comparisonTotals?.cost, {
        invertColors: true,
        percentOfRevenue: currentTotals.costPercent,
      }),
      netAdr: buildKpi(currentTotals.netAdr, comparisonTotals?.netAdr),
      netRevenue: buildKpi(
        currentTotals.netRevenue,
        comparisonTotals?.netRevenue,
        { percentOfRevenue: currentTotals.netRevenuePercent },
      ),
    };
  }

  /**
   * Build the time-series the stay-date chart consumes.
   *
   * - `granularity === 'day'`: current/comparison are paired by **position
   *   in the window**, anchored on `comparison.stayDate[0]`. A missing
   *   comparison day becomes `comparison: null`.
   * - `granularity === 'week' | 'month'`: both series are bucketed (see
   *   `bucketStayDateSeries`), then paired by **index**. Trailing current
   *   buckets without a comparison get `comparison: null`.
   *
   * `comparisonLabel` is caller-supplied; pass `null` for "no comparison".
   */
  deriveStayDate(
    periods: NetRevenuePeriods | undefined,
    comparisonLabel: string | null,
    granularity: StayDateGranularity = STAY_DATE_GRANULARITIES.DAY,
  ): StayDateData | undefined {
    if (!periods) {
      return undefined;
    }

    if (granularity === STAY_DATE_GRANULARITIES.DAY) {
      const comparisonPoints = periods.comparison?.stayDate ?? [];
      const comparisonWindowStart = comparisonPoints[0]?.date;
      const comparisonByDate = new Map(
        comparisonPoints.map((point) => [point.date, point]),
      );

      const points: StayDatePoint[] = periods.current.stayDate.map(
        (currentPoint, indexInWindow) => {
          const expectedComparisonDate = comparisonWindowStart
            ? dayjs
                .utc(comparisonWindowStart)
                .add(indexInWindow, 'day')
                .format(DATE_FORMAT)
            : null;
          const comparisonPoint = expectedComparisonDate
            ? comparisonByDate.get(expectedComparisonDate)
            : undefined;

          return {
            comparison: comparisonPoint
              ? toComparisonPoint(comparisonPoint)
              : null,
            cost: currentPoint.cost,
            label: currentPoint.date,
            netRevenue: currentPoint.roomRevenue - currentPoint.cost,
            occupancy:
              safeDivide(currentPoint.roomNights, currentPoint.capacity) ?? 0,
            roomRevenue: currentPoint.roomRevenue,
          };
        },
      );

      return { comparisonLabel, points };
    }

    const currentBuckets = bucketStayDateSeries(
      periods.current.stayDate,
      granularity,
    );
    const comparisonBuckets = periods.comparison
      ? bucketStayDateSeries(periods.comparison.stayDate, granularity)
      : [];

    const points: StayDatePoint[] = currentBuckets.map(
      (currentBucket, bucketIndex) => {
        const comparisonBucket = comparisonBuckets[bucketIndex];
        return {
          comparison: comparisonBucket
            ? toComparisonPoint(comparisonBucket)
            : null,
          cost: currentBucket.cost,
          label: currentBucket.date,
          netRevenue: currentBucket.roomRevenue - currentBucket.cost,
          occupancy:
            safeDivide(currentBucket.roomNights, currentBucket.capacity) ?? 0,
          roomRevenue: currentBucket.roomRevenue,
        };
      },
    );

    return { comparisonLabel, points };
  }
}

declare module '@ember/service' {
  interface Registry {
    'net-revenue/derive': NetRevenueDeriveService;
  }
}
